import { gsap } from 'gsap'
import { Observer } from 'gsap/Observer'

gsap.registerPlugin(Observer)

/**
 * Seeded PRNG (mulberry32). Seeded rather than Math.random so a rebuild — a
 * resize crossing a breakpoint — re-deals to the identical layout instead of
 * reshuffling the wall under the reader. gsap.utils.shuffle() uses Math.random
 * and so cannot be used here.
 */
const createRandom = (seed) => {
	let a = seed >>> 0

	return () => {
		a += 0x6d2b79f5
		let t = a
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}

/** Fisher-Yates over [0…length-1], driven by a supplied PRNG. */
const shuffledIndices = (length, random) => {
	const order = Array.from({ length }, (_, i) => i)

	for (let i = order.length - 1; i > 0; i -= 1) {
		const j = Math.floor(random() * (i + 1))
		const swap = order[i]
		order[i] = order[j]
		order[j] = swap
	}

	return order
}

/**
 * Key for "these two cards sit next to each other". Unordered, so a pair counts
 * as taken whichever way round a column stacks it.
 */
const pairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`)

/**
 * Every neighbouring pair in a column, cyclically — the loop repeats, so the
 * last card is followed by the first and that pair is as visible as any other.
 */
const cyclicPairs = (order) => {
	const pairs = []
	for (let i = 0; i < order.length; i += 1) {
		pairs.push(pairKey(order[i], order[(i + 1) % order.length]))
	}
	return pairs
}

/** How many card pairs sit next to each other in more than one column. */
const countSharedPairs = (orders) => {
	const seen = new Set()
	let shared = 0

	orders.forEach((order) => {
		cyclicPairs(order).forEach((pair) => {
			if (seen.has(pair)) shared += 1
			else seen.add(pair)
		})
	})

	return shared
}

const ORDER_ATTEMPTS = 24


/**
 * InfiniteColumns
 *
 * A wall of vertical columns that loops forever. One virtual position drives
 * every column; each column reads it at its own speed, so the wall never moves
 * as a single block.
 *
 * Motion model — drift and input are added to the same number, so there is no
 * "autoplay" mode to start and stop:
 *
 *   target  += drift * dt        // always running
 *   target  += input             // wheel / touch / keys
 *   current += (target - current) * (1 - e^(-ease * dt))
 *
 * Looping is clone-and-wrap, not cell recycling. Each column holds one `__loop`
 * of measured height H, cloned until the column is taller than the viewport
 * plus one loop. Rendering is then a single wrap per column.
 *
 * The column COUNT is not decided here — it is read from CSS by the caller and
 * passed to build(). See components/home-scroller.js.
 *
 * Expected DOM:
 *
 * <div class="home-scroller js-home-scroller">
 *   <div class="js-home-scroller-viewport"></div>   <- columns are built here
 *   <div class="js-home-scroller-deck" hidden>      <- source cards
 *     <div class="home-scroller__card">…</div>
 *   </div>
 * </div>
 */
export class InfiniteColumns {
	constructor(el, options = {}) {
		this.el = el

		this.options = {
			minPerColumn: 3, // floor; only bites with fewer cards than this in the deck
			maxPerColumn: 24, // ceiling; keeps a large deck from exploding the DOM
			// Cards a column gets beyond its fair share of the deck. Higher makes a
			// column's loop longer (less vertical repetition) at the cost of more
			// borrowed cards, i.e. more of the same work across the wall.
			fillExtra: 2,
			drift: 22, // idle speed, px/sec
			ease: 6, // lerp rate; higher catches up faster
			wheelMultiplier: 1,
			keyMultiplier: 1,
			speeds: [1, 0.86, 1.14, 0.93, 1.07], // per column, indexed modulo
			// Observer reports touch drags opposite to wheel: dragging the content
			// up is a downward scroll. Inverted by default so the two agree.
			invertTouch: true,
			// Input below this (px) does not steer the drift — a few pixels of
			// finger jitter at the end of a gesture should not reverse the wall.
			directionThreshold: 2,
			...options,
		}

		this.viewport = this.el.querySelector('.js-home-scroller-viewport')
		this.deckEl = this.el.querySelector('.js-home-scroller-deck')

		// The source deck. Read once and kept in memory: build() empties the
		// viewport on every rebuild, so it needs a pristine list to deal from.
		this.deck = Array.from(this.deckEl?.children || [])

		this.columns = []
		this.columnCount = 0

		this.target = 0
		this.current = 0
		this.velocity = 0

		this.drift = this.prefersReducedMotion() ? 0 : this.options.drift
		// Which way the wall coasts once input stops. Steered by the last
		// meaningful input, so releasing an upward scroll keeps drifting up
		// rather than snapping back to the default direction.
		this.driftDirection = 1

		this.bndRender = this.render.bind(this)
		this.bndOnKeyDown = this.onKeyDown.bind(this)
	}

	prefersReducedMotion() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}

	// ------------------------------------------------------------ //
	// BUILD
	// ------------------------------------------------------------ //

	/**
	 * Deal the deck into `columnCount` columns.
	 *
	 * Each work has ONE home column, and a column is then topped up by borrowing
	 * works homed as far away across the wall as possible. See dealOrders().
	 *
	 * Two earlier models failed, both for the same underlying reason — they let
	 * every column be able to show every work:
	 *
	 * - round-robin over a whole-copy-duplicated deck gave adjacent columns
	 *   identical three-card runs;
	 * - a full shuffled deck per column removed the runs, but the wall shows ~15
	 *   cards at once against 14 works, so with every work available to every
	 *   column duplicates on screen were guaranteed by pigeonhole. Constraining
	 *   *pairs* of cards did nothing about single duplicates side by side, which
	 *   is what actually reads as repetition.
	 */
	build(columnCount) {
		if (!this.viewport || !this.deck.length) return

		this.columnCount = columnCount
		this.viewport.innerHTML = ''
		this.columns = []

		const orders = this.dealOrders(columnCount)

		for (let i = 0; i < columnCount; i += 1) {
			const column = document.createElement('div')
			column.className = 'home-scroller__column'

			const track = document.createElement('div')
			track.className = 'home-scroller__track'

			const loop = document.createElement('div')
			loop.className = 'home-scroller__loop'

			orders[i].forEach((index) => loop.appendChild(this.deck[index].cloneNode(true)))

			track.appendChild(loop)
			column.appendChild(track)
			this.viewport.appendChild(column)

			this.columns.push({
				el: column,
				track,
				loop,
				clones: [],
				loopHeight: 0,
				speed: this.options.speeds[i % this.options.speeds.length],
				// Deterministic stagger so the columns do not all start on a card
				// boundary. Irrational-ish step keeps the offsets from repeating.
				offset: i * 137,
			})
		}

		this.measure()
	}

	/**
	 * Work out every column's card order. Returns one array of deck indices per
	 * column.
	 *
	 * 1. PARTITION. The shuffled deck is dealt out so each work gets exactly one
	 *    home column. This is what stops duplicates reading as repetition: a work
	 *    is not available to every column, so it cannot turn up in several at
	 *    once.
	 * 2. TOP UP. A fair share is only two or three cards, which loops too tightly
	 *    to scroll through, so each column borrows up to `fillExtra` more. A
	 *    borrowed work is picked for being homed FAR across the wall from the
	 *    borrowing column, and for having been borrowed least often so far — so
	 *    the copies of any one work stay as far apart, and as few, as possible.
	 * 3. ORDER. Each column's cards are then arranged so that no two cards sit
	 *    next to each other in more than one column. Two columns holding the same
	 *    pair of works stacked together read as a repeated *set* even when the
	 *    columns are far apart — which is a separate defect from a lone duplicate,
	 *    and needs its own constraint. Where a card lands vertically is not worth
	 *    controlling beyond that: the columns run at different speeds, so any two
	 *    cards pass through every relative height eventually. Horizontal distance
	 *    and pair-disjointness are the separations that hold.
	 *
	 * How many cards a column gets is then found by trying, not assumed. A longer
	 * loop means less vertical repetition but more borrowing, and past a point the
	 * borrowing has to put copies of a work in neighbouring columns — the very
	 * thing this is here to prevent. Where that point sits depends on the deck
	 * size and the column count (with a 14-work deck two extra cards per column is
	 * fine, with 8 works it is not), so the deal starts at the requested length and
	 * steps down until no work appears in two adjacent columns.
	 */
	dealOrders(columnCount) {
		const deckLength = this.deck.length
		// Seeded on the column count so each breakpoint has a stable layout of its
		// own, identical every time it is rebuilt.
		const random = createRandom(Math.imul(columnCount + 1, 0x9e3779b1))

		// -- 1. partition ------------------------------------------------ //
		// Fixed across attempts, so backing off the length does not reshuffle
		// which work belongs where.
		const shuffled = shuffledIndices(deckLength, random)
		const homeColumn = new Array(deckLength)
		const groups = Array.from({ length: columnCount }, () => [])

		shuffled.forEach((work, n) => {
			const column = n % columnCount
			homeColumn[work] = column
			groups[column].push(work)
		})

		const fairShare = Math.ceil(deckLength / columnCount)
		const floor = Math.max(1, Math.min(this.options.minPerColumn, deckLength))
		const desired = Math.min(this.options.maxPerColumn, Math.max(floor, fairShare + this.options.fillExtra))

		// Try every length and keep the best result rather than the first passable
		// one: whether a length can satisfy both properties depends on the deck
		// size and column count together, and neither is known here.
		let best = null

		for (let target = desired; target >= floor; target -= 1) {
			const sets = this.fillColumns(groups, { shuffled, homeColumn, target })
			const orders = this.orderColumns(sets, random)

			const candidate = {
				orders,
				target,
				spread: this.hasNoAdjacentDuplicates(sets, columnCount),
				sharedPairs: countSharedPairs(orders),
			}

			// Ranked: no duplicate side by side first, then no repeated pair, then
			// the longest loop. Descending target means longest already wins ties.
			const better =
				!best ||
				(candidate.spread && !best.spread) ||
				(candidate.spread === best.spread && candidate.sharedPairs < best.sharedPairs)

			if (better) best = candidate

			// Longest length that satisfies both — nothing shorter can beat it.
			if (candidate.spread && candidate.sharedPairs === 0) break
		}

		// A deck too small for either property (a two-column wall can only ever
		// place a duplicate next door; a three-card column has every one of its
		// pairs adjacent whatever the order) keeps the least-bad layout.
		return best.orders
	}

	/**
	 * Arrange each column's cards so no pair of cards is adjacent in two columns.
	 *
	 * Built card by card rather than by reshuffling until a whole permutation
	 * happens to be clean, because a column only corners itself at the end of the
	 * walk where there is no choice left — and that is escaped by restarting from
	 * a different shuffle rather than by backtracking.
	 */
	orderColumns(sets, random) {
		const usedPairs = new Set()

		return sets.map((cards) => {
			let best = null

			for (let attempt = 0; attempt < ORDER_ATTEMPTS; attempt += 1) {
				const pool = shuffledIndices(cards.length, random).map((i) => cards[i])
				const order = [pool.shift()]
				let collisions = 0

				while (pool.length) {
					const previous = order[order.length - 1]

					let pick = pool.findIndex((candidate) => !usedPairs.has(pairKey(previous, candidate)))
					if (pick === -1) {
						pick = 0 // every remaining card would repeat a pair
						collisions += 1
					}

					order.push(pool.splice(pick, 1)[0])
				}

				// The wrap pair closes the loop, so it counts too.
				if (order.length > 2 && usedPairs.has(pairKey(order[order.length - 1], order[0]))) collisions += 1

				if (!best || collisions < best.collisions) best = { order, collisions }
				if (collisions === 0) break
			}

			cyclicPairs(best.order).forEach((pair) => usedPairs.add(pair))
			return best.order
		})
	}

	/** Top each home group up to `target`, borrowing from the furthest columns. */
	fillColumns(groups, { shuffled, homeColumn, target }) {
		const borrowCount = new Array(this.deck.length).fill(0)

		return groups.map((group, column) => {
			const order = [...group]
			const present = new Set(order)

			while (order.length < target) {
				const candidates = shuffled.filter((work) => !present.has(work))

				if (!candidates.length) {
					// Every work is in this column already and it is still short, so the
					// deck is smaller than the floor and has to repeat. Cycles what the
					// column holds — `group` can be empty here, when there are fewer
					// works than columns and this one got no home card at all.
					const held = [...present]
					if (!held.length) break
					order.push(held[order.length % held.length])
					continue
				}

				candidates.sort((a, b) => {
					// Spread the load first: a work already borrowed elsewhere is a
					// worse choice than an unborrowed one however far away it lives.
					if (borrowCount[a] !== borrowCount[b]) return borrowCount[a] - borrowCount[b]
					return Math.abs(homeColumn[b] - column) - Math.abs(homeColumn[a] - column)
				})

				const pick = candidates[0]
				borrowCount[pick] += 1
				present.add(pick)
				order.push(pick)
			}

			// Returned unordered — orderColumns() decides the arrangement, because
			// that needs a view across all the columns at once.
			return order
		})
	}

	/** True when no work appears in two columns that sit next to each other. */
	hasNoAdjacentDuplicates(orders, columnCount) {
		const columnsHolding = new Map()

		orders.forEach((order, column) => {
			new Set(order).forEach((work) => {
				if (!columnsHolding.has(work)) columnsHolding.set(work, [])
				columnsHolding.get(work).push(column)
			})
		})

		for (const columns of columnsHolding.values()) {
			for (let i = 1; i < columns.length; i += 1) {
				if (columns[i] - columns[i - 1] <= 1) return false
			}
		}

		return true
	}

	/**
	 * Measure each loop and clone it until the column covers the viewport plus
	 * one full loop — the headroom that makes the wrap seamless.
	 */
	measure() {
		const viewportHeight = window.innerHeight

		this.columns.forEach((column) => {
			const height = column.loop.offsetHeight
			if (!height) return

			column.loopHeight = height

			const copiesNeeded = Math.ceil(viewportHeight / height) + 1
			const clonesNeeded = Math.max(1, copiesNeeded - 1) // the original counts as one

			while (column.clones.length < clonesNeeded) {
				const clone = column.loop.cloneNode(true)
				clone.setAttribute('aria-hidden', 'true')
				column.track.appendChild(clone)
				column.clones.push(clone)
			}
			while (column.clones.length > clonesNeeded) {
				column.clones.pop().remove()
			}
		})

		this.draw()
	}

	// ------------------------------------------------------------ //
	// RENDER
	// ------------------------------------------------------------ //
	render(time, deltaTime) {
		// Clamp: a backgrounded tab hands back a huge delta on return, which
		// would teleport the wall.
		const dt = Math.min(deltaTime, 50) / 1000

		this.target += this.drift * this.driftDirection * dt

		const previous = this.current
		// Exponential smoothing rather than a fixed lerp factor, so the feel does
		// not change between a 60Hz and a 120Hz display.
		this.current += (this.target - this.current) * (1 - Math.exp(-this.options.ease * dt))
		this.velocity = this.current - previous

		this.draw()
	}

	draw() {
		this.columns.forEach((column) => {
			if (!column.loopHeight) return

			const y = -gsap.utils.wrap(0, column.loopHeight, this.current * column.speed + column.offset)
			column.track.style.transform = `translate3d(0, ${y}px, 0)`
		})
	}

	// ------------------------------------------------------------ //
	// INPUT
	// ------------------------------------------------------------ //

	/**
	 * The one way anything moves the wall. Every source — wheel, touch, keys —
	 * goes through here so they all steer the idle drift the same way.
	 */
	addInput(delta) {
		if (!delta) return

		if (Math.abs(delta) > this.options.directionThreshold) {
			this.driftDirection = delta > 0 ? 1 : -1
		}

		this.target += delta
	}

	initInput() {
		this.observer = Observer.create({
			target: this.el,
			type: 'wheel,touch',
			preventDefault: true,
			tolerance: 0,
			dragMinimum: 3,
			lockAxis: true,
			// Let anything that scrolls on its own keep its own gestures.
			ignore: '.js-nav-overlay',
			onChangeY: (self) => {
				const isTouch = self.event?.type?.startsWith('touch') || self.event?.pointerType === 'touch'
				const direction = this.options.invertTouch && isTouch ? -1 : 1

				this.addInput(self.deltaY * this.options.wheelMultiplier * direction)
			},
		})

		window.addEventListener('keydown', this.bndOnKeyDown)
	}

	/**
	 * Observer has no keyboard support, so the wall would otherwise be
	 * unreachable without a pointer. Keys feed `target` like any other input, so
	 * they ease rather than jump.
	 */
	onKeyDown(event) {
		if (event.metaKey || event.ctrlKey || event.altKey) return

		const target = event.target
		if (target?.closest?.('input, textarea, select, [contenteditable="true"], .js-nav-overlay')) return

		const card = this.columns[0]?.loop?.firstElementChild?.offsetHeight || 300
		const page = window.innerHeight * 0.9
		let delta = 0

		switch (event.key) {
			case 'ArrowDown':
				delta = card
				break
			case 'ArrowUp':
				delta = -card
				break
			case 'PageDown':
				delta = page
				break
			case 'PageUp':
				delta = -page
				break
			case ' ':
				delta = event.shiftKey ? -page : page
				break
			case 'End':
				delta = page * 4
				break
			case 'Home':
				delta = -page * 4
				break
			default:
				return
		}

		event.preventDefault()
		this.addInput(delta * this.options.keyMultiplier)
	}

	// ------------------------------------------------------------ //
	// LIFECYCLE
	// ------------------------------------------------------------ //
	start() {
		if (this.isRunning) return
		this.isRunning = true

		this.initInput()
		gsap.ticker.add(this.bndRender)
	}

	destroy() {
		if (this.isRunning) {
			gsap.ticker.remove(this.bndRender)
			this.isRunning = false
		}

		window.removeEventListener('keydown', this.bndOnKeyDown)

		if (this.observer) {
			this.observer.kill()
			this.observer = null
		}

		if (this.viewport) this.viewport.innerHTML = ''
		this.columns = []
	}
}
